import { c as _c } from "react/compiler-runtime";
import { Fragment, type ReactNode } from "react";
type MarkdownBlock = {
  kind: "heading";
  level: 1 | 2 | 3;
  text: string;
  startLine: number;
} | {
  kind: "paragraph";
  lines: string[];
  startLine: number;
} | {
  kind: "list";
  ordered: boolean;
  start: number;
  items: Array<{
    text: string;
    line: number;
  }>;
  startLine: number;
};
const HEADING_PATTERN = /^(#{1,3})\s+(.+)$/u;
const UNORDERED_ITEM_PATTERN = /^\s*[-+*]\s+(.+)$/u;
const ORDERED_ITEM_PATTERN = /^\s*(\d+)\.\s+(.+)$/u;
const INLINE_FORMAT_PATTERN = /(\*\*[^*\n]+\*\*|__[^_\n]+__|`[^`\n]+`)/gu;
function lineBlockKind(line: string): "heading" | "unordered" | "ordered" | null {
  if (HEADING_PATTERN.test(line)) return "heading";
  if (UNORDERED_ITEM_PATTERN.test(line)) return "unordered";
  if (ORDERED_ITEM_PATTERN.test(line)) return "ordered";
  return null;
}
function parseMarkdownBlocks(text: string): MarkdownBlock[] {
  const lines = text.replace(/\r\n?/gu, "\n").split("\n");
  const blocks: MarkdownBlock[] = [];
  let lineIndex = 0;
  while (lineIndex < lines.length) {
    const line = lines[lineIndex] ?? "";
    if (line.trim().length === 0) {
      lineIndex += 1;
      continue;
    }
    const heading = line.match(HEADING_PATTERN);
    if (heading) {
      blocks.push({
        kind: "heading",
        level: heading[1].length as 1 | 2 | 3,
        text: heading[2],
        startLine: lineIndex
      });
      lineIndex += 1;
      continue;
    }
    const unordered = line.match(UNORDERED_ITEM_PATTERN);
    const ordered = line.match(ORDERED_ITEM_PATTERN);
    if (unordered || ordered) {
      const isOrdered = Boolean(ordered);
      const items: Array<{
        text: string;
        line: number;
      }> = [];
      const start = ordered ? Number(ordered[1]) : 1;
      const pattern = isOrdered ? ORDERED_ITEM_PATTERN : UNORDERED_ITEM_PATTERN;
      const startLine = lineIndex;
      while (lineIndex < lines.length) {
        const item = (lines[lineIndex] ?? "").match(pattern);
        if (!item) break;
        items.push({
          text: item[isOrdered ? 2 : 1],
          line: lineIndex
        });
        lineIndex += 1;
      }
      blocks.push({
        kind: "list",
        ordered: isOrdered,
        start,
        items,
        startLine
      });
      continue;
    }
    const paragraphStart = lineIndex;
    const paragraphLines: string[] = [];
    while (lineIndex < lines.length) {
      const paragraphLine = lines[lineIndex] ?? "";
      if (paragraphLine.trim().length === 0 || lineIndex > paragraphStart && lineBlockKind(paragraphLine)) {
        break;
      }
      paragraphLines.push(paragraphLine);
      lineIndex += 1;
    }
    blocks.push({
      kind: "paragraph",
      lines: paragraphLines,
      startLine: paragraphStart
    });
  }
  return blocks;
}
function renderInlineMarkdown(text: string): ReactNode[] {
  const content: ReactNode[] = [];
  let cursor = 0;
  for (const match of text.matchAll(INLINE_FORMAT_PATTERN)) {
    const start = match.index;
    if (start > cursor) content.push(text.slice(cursor, start));
    const token = match[0];
    if (token.startsWith("`")) {
      content.push(<code key={`code-${start}`}>{token.slice(1, -1)}</code>);
    } else {
      content.push(<strong key={`strong-${start}`}>{token.slice(2, -2)}</strong>);
    }
    cursor = start + token.length;
  }
  if (cursor < text.length) content.push(text.slice(cursor));
  return content;
}
function renderParagraphLines(lines: readonly string[]): ReactNode[] {
  return lines.map((line, index) => <Fragment key={`line-${index}-${line}`}>
            {index > 0 && <br />}
            {renderInlineMarkdown(line)}
        </Fragment>);
}
export function AssistantMessageContent(t0) {
  const $ = _c(9);
  const {
    text,
    isCoaching: t1
  } = t0;
  const isCoaching = t1 === undefined ? false : t1;
  let t2;
  let t3;
  if ($[0] !== isCoaching || $[1] !== text) {
    const blocks = parseMarkdownBlocks(text);
    t2 = "assistant-message-content";
    let t4;
    if ($[4] !== isCoaching) {
      t4 = (block, blockIndex) => {
        const key = `${block.kind}-${block.startLine}`;
        if (block.kind === "heading") {
          const content = renderInlineMarkdown(block.text);
          if (block.level === 1) {
            return <h1 key={key}>{content}</h1>;
          }
          if (block.level === 2) {
            return <h2 key={key}>{content}</h2>;
          }
          return <h3 key={key}>{content}</h3>;
        }
        if (block.kind === "list") {
          const items = block.items.map(_temp);
          return block.ordered ? <ol key={key} start={block.start}>{items}</ol> : <ul key={key}>{items}</ul>;
        }
        return <p key={key} className={isCoaching && blockIndex === 0 ? "assistant-message-lead" : undefined}>{renderParagraphLines(block.lines)}</p>;
      };
      $[4] = isCoaching;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    t3 = blocks.map(t4);
    $[0] = isCoaching;
    $[1] = text;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div className={t2}>{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function _temp(item) {
  return <li key={`item-${item.line}`}>{renderInlineMarkdown(item.text)}</li>;
}